import {
	Box,
	Card,
	CardContent,
	Divider,
	Grid,
	LinearProgress,
	Typography,
} from "@mui/material";
import { Colors } from "chart.js";
import { useEffect, useState } from "react";

import { AnalyticsData, createDataset, fetchAnalyticsData } from "./functions";
import { BarChart } from "./BarChart";
import {
	BarElement,
	CategoryScale,
	Chart as ChartJS,
	Legend,
	LinearScale,
	Title,
	Tooltip,
} from "chart.js";
import { DateRangePicker } from "./DateRangePicker";
import { MultipleOrganSelector } from "./MultipleOrganSelector";
import { useNotify } from "react-admin";
import IntervalToggleButton from "./IntervalToggleButon";

ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
	Colors
);

export type IntervalType = "month" | "year";

export const AnalyticsDashboard = () => {
	const notify = useNotify();

	const [data, setData] = useState<AnalyticsData[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [startDate, setStartDate] = useState(
		new Date(new Date().getFullYear(), 0, 1)
	);
	const [endDate, setEndDate] = useState<Date>(new Date());
	const [interval, setInterval] = useState<IntervalType>("month");
	const [allOrgans, setAllOrgans] = useState<string[]>([]);
	const [selectedOrgans, setSelectedOrgans] = useState<string[]>([]);

	useEffect(() => {
		fetchAnalyticsData(startDate, endDate, interval)
			.then((analyticsData) => {
				setData(analyticsData);

				const organs = Array.from(
					new Set(analyticsData.map((item) => Object.keys(item.organs)).flat())
				);

				if (allOrgans.length === 0) {
					setAllOrgans(organs);
					setSelectedOrgans(organs);
				}

				setLoading(false);
			})
			.catch((error) => {
				notify(error, {
					type: "error",
					messageArgs: { smart_count: 1 },
				});
			});
	}, [endDate, startDate, interval, allOrgans, notify]);

	const labels =
		interval === "month"
			? data.map((item) =>
					new Date(item.date).toLocaleString("en-US", {
						month: "long",
						year: "numeric",
					})
			  )
			: data.map((item) =>
					new Date(item.date).toLocaleString("en-US", {
						year: "numeric",
					})
			  );

	const missionsPerOrganDatasets = createDataset(
		data,
		selectedOrgans,
		"missions"
	);
	const avgDurationDatasets = createDataset(
		data,
		selectedOrgans,
		"average_duration"
	);

	return (
		<Card sx={{ mt: 2, mb: 2 }}>
			{loading ? (
				<Box padding={2}>
					<LinearProgress />
				</Box>
			) : (
				<CardContent>
					<Grid container spacing={2} padding={{ xs: 1, mobile: 2 }}>
						<Grid item xs={12}>
							<Typography variant="h1">Analytics Dashboard</Typography>
						</Grid>
						<Grid item xs={12}>
							<Divider />
						</Grid>
						<Grid
							container
							spacing={4}
							padding={{ xs: 1, mobile: 2 }}
							alignItems="center"
						>
							<Grid item>
								<DateRangePicker
									startDate={startDate}
									setStartDate={setStartDate}
									endDate={endDate}
									setEndDate={setEndDate}
								/>
							</Grid>
							<Grid item>
								<IntervalToggleButton
									interval={interval}
									setInterval={setInterval}
								/>
							</Grid>
							<Grid item>
								<MultipleOrganSelector
									allOrgans={allOrgans}
									selectedOrgans={selectedOrgans}
									setSelectedOrgans={setSelectedOrgans}
								/>
							</Grid>
						</Grid>
						<Grid item xs={12}>
							<BarChart
								chartData={{ labels, datasets: missionsPerOrganDatasets }}
								dataLabel="Number of missions"
								chartTitle="Number of missions"
							/>
						</Grid>
						<Grid item xs={12}>
							<BarChart
								chartData={{ labels, datasets: avgDurationDatasets }}
								dataLabel="Average mission duration in hours"
								chartTitle="Average mission duration"
							/>
						</Grid>
					</Grid>
				</CardContent>
			)}
		</Card>
	);
};
